import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  deleteUser as deleteAuthUser
} from 'firebase/auth';

const UserManagement = ({ user, setCurrentPage }) => {
  // State variables
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [errorMessage, setErrorMessage] = useState('');

  // New user form state
  const [newUser, setNewUser] = useState({
    displayName: '',
    email: '',
    password: '',
    instituteName: '',
    phone: '',
    subscriptionPlan: 'monthly', // Changed from subscriptionStatus
    subscriptionStatus: 'inactive', // Added separate status field
    username: '',
    watermarkText: ''
  });

  // Updated subscription plans
  const subscriptionPlans = [
    { id: 'monthly', name: 'Monthly' },
    { id: 'sixMonth', name: 'Six Month' },
    { id: 'yearly', name: 'Yearly' }
  ];

  // Subscription status options
  const subscriptionStatuses = [
    { id: 'active', name: 'Active' },
    { id: 'inactive', name: 'Inactive' }
  ];

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch users from Firestore
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const usersList = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle nested subscription object
          subscriptionPlan: data.subscription?.plan || 'monthly',
          subscriptionStatus: data.subscription?.status || 'inactive',
          subscriptionStartDate: data.subscription?.startDate,
          subscriptionEndDate: data.subscription?.endDate,
          subscriptionAmount: data.subscription?.amount,
          subscriptionOrderId: data.subscription?.orderId,
          createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date()
        };
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate username from display name
  const generateUsername = (displayName) => {
    if (!displayName) return '';
    return displayName.toLowerCase().replace(/\s+/g, '');
  };

  // Handle adding a new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    try {
      setLoading(true);
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        newUser.email, 
        newUser.password
      );
      
      // Generate username if not provided
      const username = newUser.username || generateUsername(newUser.displayName);
      
      // Add user to Firestore with nested subscription object
      const userRef = collection(db, 'users');
      await addDoc(userRef, {
        uid: userCredential.user.uid,
        displayName: newUser.displayName,
        email: newUser.email,
        instituteName: newUser.instituteName,
        phone: newUser.phone,
        subscription: {
          plan: newUser.subscriptionPlan,
          status: newUser.subscriptionStatus,
          startDate: new Date().toISOString(),
          // Set end date based on plan duration
          endDate: calculateEndDate(newUser.subscriptionPlan),
          amount: calculateAmount(newUser.subscriptionPlan),
          orderId: generateOrderId()
        },
        username: username,
        watermarkText: newUser.watermarkText || "",
        watermarkLogo: null,
        createdAt: serverTimestamp()
      });
      
      // Reset form and close modal
      setNewUser({
        displayName: '',
        email: '',
        password: '',
        instituteName: '',
        phone: '',
        subscriptionPlan: 'monthly',
        subscriptionStatus: 'inactive',
        username: '',
        watermarkText: ''
      });
      setShowAddModal(false);
      
      // Refresh user list
      fetchUsers();
      
    } catch (error) {
      console.error('Error adding user:', error);
      setErrorMessage('Failed to add user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate subscription end date based on plan
  const calculateEndDate = (plan) => {
    const now = new Date();
    
    switch(plan) {
      case 'monthly':
        return new Date(now.setMonth(now.getMonth() + 1)).toISOString();
      case 'sixMonth':
        return new Date(now.setMonth(now.getMonth() + 6)).toISOString();
      case 'yearly':
        return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString();
      default:
        return new Date(now.setMonth(now.getMonth() + 1)).toISOString();
    }
  };

  // Calculate amount based on plan
  const calculateAmount = (plan) => {
    switch(plan) {
      case 'monthly':
        return 99;
      case 'sixMonth':
        return 499;
      case 'yearly':
        return 899;
      default:
        return 99;
    }
  };

  // Generate a dummy order ID
  const generateOrderId = () => {
    return "EC" + Math.floor(Math.random() * 1000000000000000000);
  };

  // Handle updating user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      
      // Update user in Firestore with nested subscription object
      const userRef = doc(db, 'users', selectedUser.id);
      
      // If subscription plan or status has changed, update all subscription details
      let subscriptionUpdate = {};
      
      if (selectedUser.subscriptionPlan !== selectedUser.originalSubscriptionPlan || 
          selectedUser.subscriptionStatus !== selectedUser.originalSubscriptionStatus) {
        
        subscriptionUpdate = {
          plan: selectedUser.subscriptionPlan,
          status: selectedUser.subscriptionStatus,
          startDate: new Date().toISOString(),
          endDate: calculateEndDate(selectedUser.subscriptionPlan),
          amount: calculateAmount(selectedUser.subscriptionPlan),
          orderId: generateOrderId()
        };
      } else {
        // If no changes to subscription details, maintain existing values
        subscriptionUpdate = {
          plan: selectedUser.subscriptionPlan,
          status: selectedUser.subscriptionStatus,
          startDate: selectedUser.subscriptionStartDate,
          endDate: selectedUser.subscriptionEndDate,
          amount: selectedUser.subscriptionAmount,
          orderId: selectedUser.subscriptionOrderId
        };
      }
      
      await updateDoc(userRef, {
        displayName: selectedUser.displayName,
        instituteName: selectedUser.instituteName,
        phone: selectedUser.phone,
        subscription: subscriptionUpdate,
        username: selectedUser.username,
        watermarkText: selectedUser.watermarkText || ""
      });
      
      // Close modal and refresh users
      setShowEditModal(false);
      fetchUsers();
      
    } catch (error) {
      console.error('Error updating user:', error);
      setErrorMessage('Failed to update user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'users', selectedUser.id));
      
      // Close modal and refresh users
      setShowDeleteModal(false);
      fetchUsers();
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessage('Failed to delete user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal for a user
  const openEditModal = (user) => {
    setSelectedUser({
      ...user,
      // Store original values for comparison
      originalSubscriptionPlan: user.subscriptionPlan,
      originalSubscriptionStatus: user.subscriptionStatus
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Filter users based on search and subscription
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.instituteName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSubscription = filterSubscription === 'all' || user.subscriptionPlan === filterSubscription;
    
    return matchesSearch && matchesSubscription;
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Get subscription badge color class
  const getSubscriptionBadgeClass = (plan, status) => {
    if (status !== 'active') {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (plan) {
      case 'yearly':
        return 'bg-green-100 text-green-800';
      case 'sixMonth':
        return 'bg-purple-100 text-purple-800';
      case 'monthly':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format plan name for display
  const formatPlanName = (plan) => {
    switch (plan) {
      case 'sixMonth':
        return 'Six Month';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      default:
        return plan;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add User
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Search and Filter */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[300px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users or institutions..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="relative flex items-center">
            <select
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={filterSubscription}
              onChange={(e) => setFilterSubscription(e.target.value)}
            >
              <option value="all">All Subscriptions</option>
              <option value="monthly">Monthly</option>
              <option value="sixMonth">Six Month</option>
              <option value="yearly">Yearly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-indigo-700 font-medium">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.displayName || 'Unnamed User'}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.instituteName || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSubscriptionBadgeClass(user.subscriptionPlan, user.subscriptionStatus)}`}>
                        {formatPlanName(user.subscriptionPlan)} {user.subscriptionStatus === 'active' ? '' : '(Inactive)'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(user.subscriptionEndDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <button 
                          onClick={() => openEditModal(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => openDeleteModal(user)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new user.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add User
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Add New User</h2>
              
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.displayName}
                    onChange={(e) => {
                      const displayName = e.target.value;
                      setNewUser({
                        ...newUser, 
                        displayName,
                        username: generateUsername(displayName)
                      });
                    }}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                    minLength={6}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.instituteName}
                    onChange={(e) => setNewUser({...newUser, instituteName: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username (auto-generated)
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Plan
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={newUser.subscriptionPlan}
                      onChange={(e) => setNewUser({...newUser, subscriptionPlan: e.target.value})}
                    >
                      {subscriptionPlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={newUser.subscriptionStatus}
                      onChange={(e) => setNewUser({...newUser, subscriptionStatus: e.target.value})}
                    >
                      {subscriptionStatuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Watermark Text
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={newUser.watermarkText}
                    onChange={(e) => setNewUser({...newUser, watermarkText: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
                
                <div className="mt-6 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Edit User</h2>
              
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={selectedUser.displayName}
                    onChange={(e) => setSelectedUser({...selectedUser, displayName: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100"
                    value={selectedUser.email}
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={selectedUser.instituteName}
                    onChange={(e) => setSelectedUser({...selectedUser, instituteName: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={selectedUser.phone}
                    onChange={(e) => setSelectedUser({...selectedUser, phone: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={selectedUser.username}
                    onChange={(e) => setSelectedUser({...selectedUser, username: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Plan
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={selectedUser.subscriptionPlan}
                      onChange={(e) => setSelectedUser({...selectedUser, subscriptionPlan: e.target.value})}
                    >
                      {subscriptionPlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={selectedUser.subscriptionStatus}
                      onChange={(e) => setSelectedUser({...selectedUser, subscriptionStatus: e.target.value})}
                    >
                      {subscriptionStatuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Watermark Text
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={selectedUser.watermarkText || ""}
                    onChange={(e) => setSelectedUser({...selectedUser, watermarkText: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
                
                <div className="mt-6 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the user <span className="font-medium">{selectedUser.displayName || selectedUser.email}</span>? This action cannot be undone.
            </p>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;